import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Input,
  Button,
  Modal,
  Form,
  Select,
  Upload,
  message,
  Pagination
} from "antd";
import { UploadOutlined ,SearchOutlined, EnvironmentOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.extend(relativeTime);

const JobsAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
const [locationSearch, setLocationSearch] = useState("");
const [positionType, setPositionType] = useState("");

const [currentPage, setCurrentPage] = useState(1);

const [deleteModalOpen, setDeleteModalOpen] = useState(false);
const [selectedJob, setSelectedJob] = useState(null);
const [deleteConfirmText, setDeleteConfirmText] = useState("");
const [deletingJob, setDeletingJob] = useState(false);

const pageSize = 15;

  const navigate = useNavigate();   

  const [form] = Form.useForm();

  const token = localStorage.getItem("token");

  const fetchJobs = async () => {
  try {
    
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/jobs/all`,
      {
        headers: { Authorization: `Bearer ${token}`},
      }
    );

    if (res.data.success) {
      const jobsData = res.data.data;
      setJobs(jobsData);
    }
  } catch (err) {
    console.error("❌ Fetch Jobs Error:", err);
  }
};

useEffect(() => {
  fetchJobs();
}, []);

  // ✅ CREATE JOB
  const handleCreateJob = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });

      if (file) {
        formData.append("CompanyLogo", file);
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/jobs/create-job`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.data.success) {
        toast.success("Job created successfully");
        setIsModalOpen(false);
        form.resetFields();
        setFile(null);
        setPreview(null);
        fetchJobs();
      }
    } catch (err) {
      toast.error("Failed to create job");
    } finally {
      setLoading(false);
    }
  };

const filteredJobs = jobs.filter((job) => {
  const titleMatch = job.JobTitle?.toLowerCase().includes(
    jobSearch.toLowerCase()
  );

  const locationMatch = job.Location?.toLowerCase().includes(
    locationSearch.toLowerCase()
  );

  const positionMatch = positionType
    ? job.PositionType?.toLowerCase() === positionType.toLowerCase()
    : true;

  return titleMatch && locationMatch && positionMatch;
});

const paginatedJobs = filteredJobs.slice(
  (currentPage - 1) * pageSize,
  currentPage * pageSize
);

const handleDeleteJob = async () => {
  try {
    if (!selectedJob) return;

    setDeletingJob(true);

    const res = await axios.delete(
      `${process.env.REACT_APP_API_URL}/jobs/delete-job/${selectedJob.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (res.data.success) {
      toast.success("Job deleted successfully");
      setDeleteModalOpen(false);
      setSelectedJob(null);
      setDeleteConfirmText("");
      fetchJobs();
    } else {
      toast.error(res.data.message || "Failed to delete job");
    }
  } catch (err) {
    console.error("Delete Job Error:", err);
    toast.error(
      err?.response?.data?.message || "Failed to delete job"
    );
  } finally {
    setDeletingJob(false);
  }
};

  return (
    <div className="p-6">

      <ToastContainer
              position="bottom-left"
              autoClose={5000}
              hideProgressBar={true}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="dark"
              className="!mb-6 !ml-4"
              toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
            />

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">
          All Jobs
        </h1>

        {/* POST JOB BUTTON */}
        <Button
          type="primary"
          onClick={() => setIsModalOpen(true)}
          className="font-semibold text-md"
        >
          + Post a Job
        </Button>
      </div>

      {/* SEARCH */}
   <div className="p-5 mb-6 border shadow-sm bg-white/80 backdrop-blur rounded-2xl">

  {/* HEADER */}
  <div className="mb-4">
    <h2 className="text-lg font-semibold text-slate-800">
      Search Jobs
    </h2>
    <p className="text-sm text-gray-500">
      Filter jobs by Job Title , Location , Position Type 
    </p>
  </div>

  {/* INPUTS */}
 <div className="grid grid-cols-1 gap-4 md:grid-cols-4">

  {/* JOB TITLE */}
  <div>
    <label className="block mb-1 text-xs font-semibold text-gray-500">
      Job Title
    </label>
    <Input
      placeholder="e.g. React Developer"
      className="h-11 rounded-xl"
      value={jobSearch}
      onChange={(e) => setJobSearch(e.target.value)}
    />
  </div>

  {/* LOCATION */}
  <div>
    <label className="block mb-1 text-xs font-semibold text-gray-500">
      Location
    </label>
    <Input
      placeholder="e.g. Bangalore / Remote"
      className="h-11 rounded-xl"
      value={locationSearch}
      onChange={(e) => setLocationSearch(e.target.value)}
    />
  </div>

  {/* POSITION TYPE */}
  <div>
    <label className="block mb-1 text-xs font-semibold text-gray-500">
      Position Type
    </label>
    <Select
      placeholder="Select Position Type"
      className="w-full h-11 rounded-xl"
      value={positionType || undefined}
      onChange={(value) => setPositionType(value || "")}
      allowClear
      size="large"
    >
      <Select.Option value="FRESHER">FRESHER</Select.Option>
      <Select.Option value="EXPERIENCE">EXPERIENCE</Select.Option>
      
    </Select>
  </div>

  {/* CLEAR BUTTON */}
  <div className="flex items-end">
    <Button
      onClick={() => {
        setJobSearch("");
        setLocationSearch("");
        setPositionType("");
      }}
      className="w-full font-medium h-11 rounded-xl"
    >
      Clear Filters
    </Button>
  </div>

</div>

</div>

      {/* JOB LIST */}
      <div className="space-y-5">
       {paginatedJobs.map((job) => (
            
          <Card key={job.id} className="transition border rounded-2xl hover:shadow-md">
            <div className="flex items-start justify-between">

              {/* LEFT */}
              <div className="flex gap-4">

                <img
                  src={job.CompanyLogo}
                  alt="logo"
                  className="object-cover w-12 h-12 rounded-md"
                />

                <div>
                  <div className="flex items-center gap-3">

  <h2
    onClick={() =>
      navigate(`/academy/admin/jobs/${job.id}/job-details`)
    }
    className="text-lg font-semibold cursor-pointer text-slate-800 hover:text-blue-600 hover:underline"
  >
    {job.JobTitle}
  </h2>

  {/* Position Type Tag */}
  {job.PositionType && (
   <span
  className={`px-2 py-1 text-xs font-semibold rounded-md border flex items-center gap-1 ${
    job.PositionType.toUpperCase() === "FRESHER"
      ? "bg-green-50 text-green-600 border-green-200"
      : "bg-blue-50 text-blue-600 border-blue-200"
  }`}
>
  {job.PositionType.toUpperCase() === "FRESHER" ? "🟢" : "🔵"} {job.PositionType.toUpperCase()}
</span>
  )}

</div>


                  <p className="text-sm text-gray-500">
                    {job.CompanyName} • {job.WorkPreference} • {job.Location}
                  </p>

                  {/* EXPERIENCE */}
                  <p className="mt-1 text-sm text-gray-600">
                    <span className="font-semibold text-gray-500">Experience Required : </span>{job.Experience}
                  </p>

                 
                  {/* SKILLS */}
                  <div className="flex flex-wrap gap-2 mt-3">
  {job.RequiredSkills?.split(",").map((skill, i) => {
    const colors = [
      "bg-blue-50 text-blue-600 border-blue-200",
      "bg-green-50 text-green-600 border-green-200",
      "bg-purple-50 text-purple-600 border-purple-200",
      "bg-orange-50 text-orange-600 border-orange-200",
      "bg-pink-50 text-pink-600 border-pink-200",
      "bg-indigo-50 text-indigo-600 border-indigo-200",
    ];

    const colorClass = colors[i % colors.length];

    return (
      <span
        key={i}
        className={`px-3 py-1 text-xs font-semibold border rounded-md ${colorClass} transition hover:shadow-sm`}
      >
        {skill.trim()}
      </span>
    );
  })}
</div>
                </div>
              </div>

              {/* RIGHT */}
<div className="flex flex-col items-end gap-3">
  <p className="text-xs font-semibold text-gray-500">
    {dayjs(job.CreatedAt)
      .add(5, "hour")
      .add(30, "minute")
      .fromNow()}
  </p>

  <div className="flex items-center gap-2">
    <Button
      type="primary"
      onClick={() => {
        const website = job.Website?.trim();

        if (website && website !== "null" && website !== "undefined") {
          window.open(website, "_blank");
        } else if (job.ContactEmail) {
          window.location.href = `mailto:${job.ContactEmail}`;
        } else {
          alert("No application method available");
        }
      }}
    >
      Apply
    </Button>

    <Button
      danger
      icon={<DeleteOutlined />}
      className="font-semibold text-white !bg-red-500 hover:!bg-red-600 border-red-500 hover:!border-red-600"
      onClick={() => {
        setSelectedJob(job);
        setDeleteConfirmText("");
        setDeleteModalOpen(true);
      }}
    >
      Delete
    </Button>
  </div>
</div>

            </div>
          </Card>
        ))}
      </div>


      <Modal
  open={deleteModalOpen}
  onCancel={() => {
    setDeleteModalOpen(false);
    setSelectedJob(null);
    setDeleteConfirmText("");
  }}
  footer={null}
  centered
  width={520}
  title={
    <span className="text-xl font-bold text-red-600">
      Delete Job
    </span>
  }
>
  {selectedJob && (
    <div className="space-y-5">
      <div className="p-4 border border-red-200 bg-red-50 rounded-2xl">
        <p className="text-sm font-semibold text-gray-700">
          You are about to delete this job:
        </p>

        <h2 className="mt-2 text-lg font-bold text-slate-800">
          {selectedJob.JobTitle}
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          {selectedJob.CompanyName} • {selectedJob.Location}
        </p>
      </div>

      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Type this exact job title to enable delete:
        </label>

        <div className="px-3 py-2 mb-3 font-semibold border rounded-xl bg-gray-50 text-slate-800">
          {selectedJob.JobTitle}
        </div>

        <Input
          placeholder="Enter exact Job Title"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          className="h-11 rounded-xl"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button
          onClick={() => {
            setDeleteModalOpen(false);
            setSelectedJob(null);
            setDeleteConfirmText("");
          }}
          className="h-10 rounded-xl"
        >
          Cancel
        </Button>

        <Button
          danger
          type="primary"
          loading={deletingJob}
          disabled={deleteConfirmText !== selectedJob.JobTitle}
          onClick={handleDeleteJob}
          className="font-semibold text-white !bg-red-500 hover:!bg-red-600 border-red-500 hover:!border-red-600 h-10 rounded-xl"
        >
          Delete Job
        </Button>
      </div>
    </div>
  )}
</Modal>

      <Modal
  open={isModalOpen}
  onCancel={() => setIsModalOpen(false)}
  footer={null}
  width={750}
  centered
  title={<span className="text-xl font-bold text-slate-800">Post a Job</span>}
>
  <Form layout="vertical" form={form} onFinish={handleCreateJob}>

    <div className="grid grid-cols-2 gap-4">

      <Form.Item name="CompanyName" label="Company Name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item name="JobTitle" label="Job Title" rules={[{ required: true }]}>
        <Input />
      </Form.Item>

      <Form.Item name="Experience" label="Experience"
      rules={[{ required: true }]}>
        <Input placeholder="10+ Years" />
      </Form.Item>

      <Form.Item name="WorkPreference" label="Work Preference"
      rules={[{ required: true }]}>
        <Input placeholder="Hybrid or Remote" />
      </Form.Item>

      <Form.Item name="Location" label="Location"
      rules={[{ required: true }]}>
        <Input placeholder="Chennai, India" />
      </Form.Item>

      <Form.Item name="Website" label="Apply Link"
    >
        <Input placeholder="https://company.com/careers" />
      </Form.Item>

      <Form.Item name="ContactEmail" label="Contact Email"
      rules={[{ required: false }]}>
        <Input />
      </Form.Item>

      <Form.Item name="ContactPhone" label="Contact Phone"
      rules={[{ required: false }]}>
        <Input />
      </Form.Item>

      <Form.Item
  name="PositionType"
  label="Position Type"
  rules={[{ required: true, message: "Please select position type" }]}
>
  <Select placeholder="Select position type">
    <Select.Option value="FRESHER">FRESHER</Select.Option>
    <Select.Option value="EXPERIENCE">EXPERIENCE</Select.Option>
  </Select>
</Form.Item>

    </div>

    {/* JOB SUMMARY */}
    <Form.Item
      name="JobSummary"
      label="Job Summary"
      rules={[{ required: true }]}
    >
      <Input.TextArea
        rows={6}
        placeholder={`Job Summary:
- Write responsibilities like this
- Each line will be preserved
- Use "-" for bullets`}
      />
    </Form.Item>

    {/* ABOUT THE COMPANY */}
<Form.Item
  name="AboutTheCompany"
  label="About The Company"
  rules={[{ required: true, message: "Please enter company details" }]}
>
  <Input.TextArea
    rows={5}
    placeholder={`About the Company:
- Brief intro about company
- Mission / vision
- Culture / environment`}
  />
</Form.Item>


    {/* DESIRED SKILLS */}
    <Form.Item
      name="DesiredSkills"
      label="Desired Skills & Experience"
      rules={[{ required: true }]}
    >
      <Input.TextArea
        rows={5}
        placeholder={`- Experience with SAST tools
- Knowledge of OWASP Top 10
- Secure coding practices`}
      />
    </Form.Item>

    {/* REQUIRED SKILLS */}
<Form.Item
  name="RequiredSkills"
  label="Required Skills"
  rules={[{ required: true, message: "Please enter required skills" }]}
>
  <Input
    placeholder="e.g. React, Node.js, AWS, Docker (comma separated)"
  />
</Form.Item>

    {/* LOGO UPLOAD */}
    <Form.Item label="Company Logo">
      <Upload
        beforeUpload={(file) => {
          setFile(file);
          setPreview(URL.createObjectURL(file));
          return false;
        }}
        showUploadList={false}
      >
        <Button>Select Logo</Button>
      </Upload>

      {preview && (
        <img
          src={preview}
          alt="preview"
          className="w-20 h-20 mt-3 rounded-lg"
        />
      )}
    </Form.Item>

    <Button
      htmlType="submit"
      type="primary"
      block
      className="mt-2 font-semibold h-11"
    >
      Create Job
    </Button>

  </Form>
</Modal>

<div className="flex justify-center mt-10">

  <Pagination
    current={currentPage}
    pageSize={pageSize}
    total={filteredJobs.length}
    onChange={(page) => setCurrentPage(page)}
    showSizeChanger={false}
    className="custom-pagination"
  />

</div>

    </div>
  );
};

export default JobsAdmin;